/* eslint-disable */
import React, { useEffect, useState } from "react";
import {
  Paper,
  Group,
  Text,
  Anchor,
  ActionIcon,
  Switch,
  createStyles,
  Grid,
  Container,
  Button,
  Loader,
  Title,
  Card,
  Select,
  Badge,
  Tabs,
  Tooltip,
  Box,
} from "@mantine/core";
import { IconSettings, IconBuilding, IconUsers, IconUser, IconBuildingCommunity } from "@tabler/icons-react";
import {
  useGetLeaveConfiguration,
  LeaveConfigLevel,
} from "api/leaveConfiguration/getLeaveConfig";
import { useUpdateLeaveConfiguration } from "api/leaveConfiguration/leaveConfigUpdate";
import { showNotification } from "@mantine/notifications";
import AddCustomPolicyModal from "./components/customPolicy";
import UpdateLeaveConfigModal from "./components/addCustomPolicy";
import { useUser } from "hooks/useUser";
import { PermissionButton } from "api/permission/buttonpermission";
import { PermissionActionIcon } from "api/permission/permissionActionBtn";
import PermissionSwitch from "api/permission/permissionSwitch";
import { useGetDepartments } from "api/department/getAllDepartment";
import { useGetPrograms } from "api/program/getprogram";
import { useGetAllOrgUsers } from "api/organizations/getAllOrgUsers";

const rem = (px: any) => `${px / 16}rem`;

const useStyles = createStyles((theme) => ({
  grid: {
    width: "100%",
    marginTop: rem(16),
  },
  card: {
    padding: `${rem(18)} ${rem(20)}`,
    borderRadius: rem(8),
    border: `1px solid ${
      theme.colorScheme === "dark" ? theme.colors.dark[4] : "#edeef0"
    }`,
    boxShadow: "none",
    minHeight: rem(72),
    display: "flex",
    alignItems: "center",
  },
  leftGroup: {
    display: "flex",
    alignItems: "center",
    gap: rem(12),
  },
  leaveLabel: {
    fontSize: "12px",
  },
  rightGroup: {
    display: "flex",
    alignItems: "center",
    gap: rem(10),
  },
  customPolicy: {
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: rem(13),
  },
  switchRoot: {
    padding: 0,
    margin: 0,
  },
  switchInput: {
    width: rem(36),
    height: rem(20),
  },
  icon: {
    width: rem(28),
    height: rem(28),
  },
  levelSelector: {
    marginBottom: rem(16),
  },
  entitySelect: {
    maxWidth: 350,
  },
  inheritBadge: {
    fontSize: rem(10),
    fontWeight: 500,
    textTransform: "none" as any,
  },
}));

const LEAVE_DEFINITIONS: { key: string; label: string }[] = [
  { key: "annualLeave", label: "Annual Leave" },
  { key: "sickLeave", label: "Sick Leave" },
  { key: "pressingNecessity", label: "Pressing Necessity" },
  { key: "compassionateLeave", label: "Compassionate Leave" },
  { key: "bereavementLeave", label: "Bereavement Leave" },
  { key: "juryDutyLeave", label: "Jury Duty" },
  { key: "electionLeave", label: "Election Leave" },
  { key: "bandBusinessLeave", label: "Band Business Leave" },
  { key: "governanceLeave", label: "Governance Leave" },
  { key: "educationLeave", label: "Education Leave" },
  { key: "professionalDevelopmentLeave", label: "Professional Development" },
  { key: "culturalSpiritualLeave", label: "Cultural / Spiritual Leave" },
  { key: "leaveWithoutPay", label: "Leave Without Pay (LOP)" },
];

type LocalLeave = { id: string; key: string; name: string; active: boolean };

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const y = CURRENT_YEAR - 2 + i;
  return { value: String(y), label: String(y) };
});

export default function LeaveConfig() {
  const { classes } = useStyles();
  const [opened, setOpened] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const { user } = useUser();
  const resolvedOrgForHook = user?.org?._id;
  const [selectedLeaveKey, setSelectedLeaveKey] = useState<string | null>(null);

  // Year state
  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_YEAR);

  // Multi-level state
  const [activeLevel, setActiveLevel] = useState<LeaveConfigLevel>("organization");
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);

  // Fetch entities for dropdowns
  const { data: departmentsData } = useGetDepartments(1, 100);
  const { data: programsData } = useGetPrograms();
  const { data: usersData } = useGetAllOrgUsers();

  // Reset entity when level changes
  useEffect(() => {
    setSelectedEntityId(null);
  }, [activeLevel]);

  // Build entity select data
  const entitySelectData = React.useMemo(() => {
    switch (activeLevel) {
      case "department":
        return (departmentsData?.data || []).map((d) => ({
          value: d._id,
          label: d.name,
        }));
      case "program":
        return (programsData?.data || []).map((p) => ({
          value: p._id,
          label: p.name,
        }));
      case "user":
        return (usersData?.data || []).map((u: any) => ({
          value: u._id,
          label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || u._id,
        }));
      default:
        return [];
    }
  }, [activeLevel, departmentsData, programsData, usersData]);

  // Determine if we should fetch config
  const shouldFetch =
    activeLevel === "organization" || !!selectedEntityId;

  const { data, isLoading, isError } = useGetLeaveConfiguration(
    shouldFetch ? resolvedOrgForHook : null,
    activeLevel,
    selectedEntityId || undefined,
    selectedYear
  );

  const config = data?.data;
  const isInherited = activeLevel !== "organization" && config === null;

  const [leaves, setLeaves] = useState<LocalLeave[]>(
    LEAVE_DEFINITIONS.map((d, i) => ({
      id: `${i}`,
      key: d.key,
      name: d.label,
      active: false,
    }))
  );

  const { mutate: updateLeaveConfiguration, isLoading: isUpdating } =
    useUpdateLeaveConfiguration(resolvedOrgForHook!);

  useEffect(() => {
    if (!config) {
      // Reset all to inactive if no config at this level
      setLeaves(
        LEAVE_DEFINITIONS.map((d, i) => ({
          id: `${i}`,
          key: d.key,
          name: d.label,
          active: false,
        }))
      );
      return;
    }

    const mapped = LEAVE_DEFINITIONS.map((def, idx) => {
      const value = (config as any)[def.key];
      let enabled = false;

      if (typeof value === "boolean") {
        enabled = value;
      } else if (value && typeof value === "object" && "enabled" in value) {
        enabled = !!value.enabled;
      } else {
        enabled = false;
      }

      return {
        id: `${idx}`,
        key: def.key,
        name: def.label,
        active: enabled,
      } as LocalLeave;
    });

    setLeaves(mapped);
  }, [config]);

  const handleToggle = (key: string) => {
    setLeaves((prev) =>
      prev.map((l) => (l.key === key ? { ...l, active: !l.active } : l))
    );

    if (!resolvedOrgForHook) {
      showNotification({
        title: "Update failed",
        message: "No organization selected. Unable to persist change.",
        color: "red",
      });
      return;
    }

    const currentValue = (config as any)?.[key];
    let sectionPayload: any;

    if (currentValue === undefined || currentValue === null) {
      sectionPayload = { enabled: true };
    } else if (typeof currentValue === "boolean") {
      sectionPayload = !currentValue;
    } else if (typeof currentValue === "object") {
      try {
        sectionPayload = JSON.parse(JSON.stringify(currentValue));
      } catch {
        sectionPayload = { ...currentValue };
      }
      if ("enabled" in sectionPayload) {
        sectionPayload.enabled = !Boolean(sectionPayload.enabled);
      } else {
        sectionPayload.enabled = !Boolean((sectionPayload as any).enabled);
      }
    } else {
      sectionPayload = { enabled: true };
    }

    const payload: any = {
      level: activeLevel,
      year: selectedYear,
      ...(activeLevel !== "organization" && selectedEntityId
        ? { entityId: selectedEntityId }
        : {}),
      [key]: sectionPayload,
    };

    updateLeaveConfiguration(
      { id: resolvedOrgForHook, data: payload },
      {
        onSuccess: () => {
          showNotification({
            title: "Saved",
            message: `${key} updated successfully at ${activeLevel} level.`,
          });
        },
      }
    );
  };

  if (isLoading && activeLevel === "organization") {
    return (
      <Container size="lg" style={{ textAlign: "center", paddingTop: rem(40) }}>
        <Loader />
        <Text mt="sm">Loading leave configuration…</Text>
      </Container>
    );
  }

  if (isError && activeLevel === "organization") {
    return (
      <Container size="lg" style={{ textAlign: "center", paddingTop: rem(40) }}>
        <Text color="red">Failed to load leave configuration.</Text>
        <Button mt="md" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <div>
      <Group position="apart" align="center">
        <Title order={3}>Leave Configuration</Title>
        <Select
          label="Year"
          data={YEAR_OPTIONS}
          value={String(selectedYear)}
          onChange={(val) => val && setSelectedYear(Number(val))}
          style={{ width: 110 }}
        />
      </Group>

      {/* Level Selector Tabs */}
      <Card withBorder mt="md" mb="md" p="sm">
        <Tabs
          value={activeLevel}
          onTabChange={(val) => setActiveLevel(val as LeaveConfigLevel)}
        >
          <Tabs.List>
            <Tabs.Tab value="organization" icon={<IconBuilding size={14} />}>
              Organization
            </Tabs.Tab>
            <Tabs.Tab value="department" icon={<IconBuildingCommunity size={14} />}>
              Department
            </Tabs.Tab>
            <Tabs.Tab value="program" icon={<IconUsers size={14} />}>
              Program
            </Tabs.Tab>
            <Tabs.Tab value="user" icon={<IconUser size={14} />}>
              User
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {/* Entity selector for non-organization levels */}
        {activeLevel !== "organization" && (
          <Box mt="md">
            <Select
              className={classes.entitySelect}
              label={`Select ${activeLevel}`}
              placeholder={`Choose a ${activeLevel}...`}
              searchable
              clearable
              data={entitySelectData}
              value={selectedEntityId}
              onChange={setSelectedEntityId}
              nothingFound={`No ${activeLevel}s found`}
            />
          </Box>
        )}
      </Card>

      {/* Show info if no entity selected for non-org levels */}
      {activeLevel !== "organization" && !selectedEntityId && (
        <Card withBorder mt="md" mb="lg" p="lg">
          <Text color="dimmed" align="center" size="sm">
            Select a {activeLevel} above to view or configure leave settings.
          </Text>
        </Card>
      )}

      {/* Show inherited notice */}
      {isInherited && selectedEntityId && (
        <Card withBorder mt="md" mb="md" p="md" style={{ borderColor: "#FFB347" }}>
          <Group spacing="xs">
            <Badge color="orange" variant="light" className={classes.inheritBadge}>
              Inherited
            </Badge>
            <Text size="sm" color="dimmed">
              No override exists at this level. Organization defaults apply.
              Toggle any setting below to create an override for this {activeLevel}.
            </Text>
          </Group>
        </Card>
      )}

      {/* Loading state for non-org levels */}
      {isLoading && activeLevel !== "organization" && selectedEntityId && (
        <Container size="lg" style={{ textAlign: "center", paddingTop: rem(20) }}>
          <Loader size="sm" />
          <Text mt="xs" size="sm">Loading configuration…</Text>
        </Container>
      )}

      {/* Leave cards */}
      {(activeLevel === "organization" || selectedEntityId) && !isLoading && (
        <Card withBorder mt="md" mb="lg" p="md">
          <Grid gutter="xl" className={classes.grid}>
            {leaves.map((leave) => (
              <Grid.Col key={leave.id} sm={6} md={6} xl={4}>
                <Paper className={classes.card} radius="sm">
                  <Group position="apart" style={{ width: "100%" }}>
                    <Group className={classes.leftGroup} spacing="xs">
                      <PermissionSwitch
                        checked={leave.active}
                        onChange={() => handleToggle(leave.key)}
                        size="md"
                        classNames={{
                          root: classes.switchRoot,
                          input: classes.switchInput,
                        }}
                        styles={(theme: any) => ({
                          track: {
                            backgroundColor: leave.active ? "#ff7a00" : "#e9ecef",
                            border: "none",
                          },
                          thumb: {
                            backgroundColor: "#fff",
                            boxShadow: "none",
                          },
                        })}
                        permission="employees.leaveConfiguration"
                        tooltipIfNo="You are not allowed to change this setting"
                      />

                      <Text weight={700} className={classes.leaveLabel}>
                        {leave.name}
                      </Text>
                    </Group>

                    <Group className={classes.rightGroup} spacing={4}>
                      <Anchor className={classes.customPolicy} component="button">
                        Custom Policy
                      </Anchor>
                      <PermissionActionIcon
                        permission="employees.leaveConfiguration"
                        variant="transparent"
                        className={classes.icon}
                        onClick={() => {
                          setSelectedLeaveKey(leave.key);
                          setOpened(true);
                        }}
                      >
                        <IconSettings size={18} />
                      </PermissionActionIcon>
                    </Group>
                  </Group>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        </Card>
      )}

      <UpdateLeaveConfigModal
        opened={opened}
        selectedLeaveKey={selectedLeaveKey}
        onClose={() => {
          setOpened(false);
          setSelectedLeaveKey(null);
        }}
        orgId={resolvedOrgForHook}
        configLevel={activeLevel}
        entityId={selectedEntityId || undefined}
        configYear={selectedYear}
      />

      <AddCustomPolicyModal
        addOpen={addOpen}
        onClose={() => setAddOpen(false)}
      />
    </div>
  );
}
